// @flow
import type {AbstractComponent, ComponentType} from 'react'
import React, {Component} from 'react'
import {createFragmentContainer} from 'react-relay'
import type {$RelayProps} from 'react-relay'
import {debounce} from 'underscore'
import {lensPath, set} from 'ramda'

import Section from 'components/proposals/ui/Section'
import Toolbar from 'components/proposals/ui/Toolbar'
import type {IMutator} from './Mutator'
import {NullMutator} from './Mutator'
import removeRow from './removeRow'

import type {Alert, Change} from '../types'

type Props = $ReadOnly<{
  change: Change,
  isReadonly: boolean,
  mutator: IMutator,
  organization: Object,
  proposal: Object,
  showSummary?: boolean,
}>

type State = $ReadOnly<{
  hasChanges: boolean,
  hasTouched: boolean,
  showSummary: boolean,
}>

function createPartComponent(
  Strategy: Object,
  Editor: ComponentType<*>,
  Summary: ComponentType<*>,
): AbstractComponent<$RelayProps<React$ElementConfig<any>>, any> {
  class Part extends Component<Props, State> {
    static defaultProps = {
      isReadonly: true,
      mutator: new NullMutator(),
      showSummary: false,
    }

    static getDerivedStateFromProps(props: Props, state: State) {
      if (!props.isReadonly)
        return null

      return {
        ...state,
        ...Strategy.fieldsFromChange(props.change),
      }
    }

    constructor(props: Props) {
      super(props)
      this.mutationCanceled = false
      this.debouncedMutateChange = debounce(this.mutateChange, 500)
      this.strategy = new Strategy(props.change)
      this.state = {
        hasChanges: false,
        hasTouched: false,
        showSummary: props.showSummary,
        ...Strategy.fieldsFromChange(props.change),
      }
    }

    componentWillUnmount() {
      if (this.props.isReadonly)
        return
      this.mutationCanceled = true
      if (this.state.hasChanges)
        this.props.mutator.updateChange(this.changePayload)
    }

    onChange = (path: string[], value: string) => {
      this.setState(
        (prevState) => set(lensPath(path), value, {hasChanges: true, ...prevState}),
        this.debouncedMutateChange,
      )
    }

    onDelete = (path: string) => {
      const [collection, index] = path
      this.setState((prevState) => ({
        [collection]: removeRow(prevState[collection], index),
      }), this.debouncedMutateChange)
    }

    onDeleteChange = () => {
      // When the component unmounts, there should be no attempted update of the
      // proposal change once it's been deleted.  We ensure that by setting
      // hasChanges: false.
      this.setState({
        hasChanges: false,
      }, this.props.mutator.deleteChange)
    }

    get change(): Change {
      return this.props.change
    }

    get changeKey() {
      return this.strategy.changeKey
    }

    get isNew() {
      return /^Create/.test(this.change.type)
    }

    get onClickDeleteFn() {
      return this.props.isReadonly ? null : this.onDeleteChange
    }

    get changePayload() {
      return this.strategy.changePayload(this.state)
    }

    get alerts(): $ReadOnlyArray<Alert> {
      return this.state.hasTouched && !this.props.isReadonly
        ? this.change.alerts
        : []
    }

    get baseAlert(): ?Alert {
      return this.pickAlert('base')
    }

    strategy: Object
    debouncedMutateChange: Function
    mutationCanceled: boolean

    pickAlert = (path: string): ?Alert => this.alerts.find((alert) => alert.path === path)

    mergeUpdate = (update) => {
      this.setState(update, this.debouncedMutateChange)
    }

    mutateChange = () => {
      if (this.mutationCanceled)
        return

      this.props.mutator.updateChange(this.changePayload).then(() => {
        this.setState({hasTouched: true})
      })
    }

    changeSummaryState = (newState: boolean) => {
      this.setState({showSummary: newState})
    }

    renderViewer = () => (
      <Editor
        {...this.state}
        {...this.props}
        alerts={this.alerts}
        changeKey={this.changeKey}
        isNew={this.isNew}
        isReadonly
        onChange={() => {}}
        pickAlert={this.pickAlert}
      />
    )

    renderEditor = () => (
      <Editor
        {...this.state}
        {...this.props}
        alerts={this.alerts}
        changeKey={this.changeKey}
        isNew={this.isNew}
        mergeUpdate={this.mergeUpdate}
        onDelete={this.onDelete}
        onChange={this.onChange}
        pickAlert={this.pickAlert}
      />
    )

    renderSummary = () => (
      <Summary
        {...this.state}
        change={this.props.change}
        className="test-container"
        changeKey={this.changeKey}
      />
    )

    renderContents = () => {
      if (this.state.showSummary)
        return this.renderSummary()

      if (this.props.isReadonly)
        return this.renderViewer()

      return this.renderEditor()
    }

    // eslint-disable-next-line react/no-arrow-function-lifecycle
    render = () => (
      <Section
        alert={this.baseAlert}
        changeKey={this.changeKey}
        className={this.strategy.className}
        description={this.strategy.partDescription}
        key={this.changeKey}
        title={this.strategy.partTitle}
        error={this.strategy.error}
        // eslint-disable-next-line react/no-unstable-nested-components
        rightButton={() => (
          <Toolbar
            onClickDelete={this.onClickDeleteFn}
            isSummaryShown={this.state.showSummary}
            showSummary={() => this.changeSummaryState(true)}
            hideSummary={() => this.changeSummaryState(false)}
          />
        )}
      >
        {this.renderContents()}
      </Section>
    )
  }

  Part.displayName = `Part(${Strategy.displayName})`

  return createFragmentContainer(Part, Strategy.query)
}

export default createPartComponent
